import { IEnterpriseCertification } from '@/services/enterpriseCertification/interface';
import type { CurrentUserInfo } from '@/typings/user';
import { Modal, Tag } from '@bluevision/components';
import { openWindowInSafe, useRequest, type SimpleFunc } from '@bluevision/tools';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './index.less';
import { Card } from 'antd';
import CDescriptions from '@/components/CDescriptions';
import { getCertificationLink } from '@/services/enterpriseCertification';

interface CertificationInformationProps {
  show: boolean;
  onClose: SimpleFunc;
  certificationType?: IEnterpriseCertification.Common.CertificationType;
  userInfo?: CurrentUserInfo;
}

const CertificationInformation: React.FC<CertificationInformationProps> = ({
  show,
  certificationType,
  onClose,
  userInfo,
}) => {
  const { t } = useTranslation();

  // 企业认证
  const isEnterprise = useMemo(
    () => certificationType === IEnterpriseCertification.Common.CertificationType.ENTERPRISE,
    [certificationType],
  );
  const certificationTitle = useMemo(() => {
    return isEnterprise ? t('enterprise_authentication') : t('real_name_authentication_simple');
  }, [isEnterprise, t]);
  // 认证信息
  const certificationInfo = useMemo(() => {
    return isEnterprise
      ? userInfo?.signEnterpriseInfo?.certificationInfos
      : userInfo?.signIndividualInfo?.certificationInfos;
  }, [isEnterprise, userInfo]);
  // 企业认证额外展示信息
  const enterpriseOption = useMemo(() => {
    return isEnterprise
      ? [
          {
            title: t('certified_enterprise'),
            key: 'certifiedEnterprise',
          },
        ]
      : [];
  }, [isEnterprise, t]);

  // 获取认证链接
  const { request, loading } = useRequest(getCertificationLink, (res) => {
    if (res?.short_link) {
      openWindowInSafe(res.short_link, '_self');
    }
  });

  return (
    <Modal
      open={show}
      title={certificationTitle}
      onCancel={onClose}
      maskClosable={false}
      bodyStyle={{ maxHeight: 510, overflowY: 'auto' }}
      onOk={() =>
        request({
          certification_type: certificationType,
          redirect_url: window.location.href,
        })
      }
      okText={t('add_certification')}
      okButtonProps={{
        loading,
      }}
    >
      <div className='flex-align-items flex-column' style={{ gap: 8 }}>
        {certificationInfo?.map((item) => (
          <Card
            key={item.id}
            className={styles['certification-card']}
            title={certificationTitle}
            extra={<Tag color='green'>{t('certified')}</Tag>}
          >
            <CDescriptions
              column={1}
              bordered={false}
              textAlign='left'
              colon={false}
              className={styles['certification-descriptions']}
              options={[
                {
                  title: t('authentication_id'),
                  key: 'id',
                },
                {
                  title: t('certified_person'),
                  key: 'certifier',
                },
                ...enterpriseOption,
                {
                  title: t('certification_time'),
                  key: 'certifiedTime',
                },
              ]}
              data={item}
            />
          </Card>
        ))}
      </div>
    </Modal>
  );
};

export default CertificationInformation;
